import React, { useMemo, useRef, useState } from 'react';
import { styled } from 'linaria/react';
import { cx } from 'linaria';
import { Link } from 'react-router-dom';
import SlickSlider from 'react-slick';
import { theme } from '../Theme';
import { ReactComponent as Carrot } from '../../svg/Carrot.svg';

import 'slick-carousel/slick/slick.css';

const Wrapper = styled('nav')`
  width: 100%;
  position: relative;
  max-width: 90rem;
  margin: 0 auto;
  padding: 0 12px;
`;

const SliderShell = styled('div')`
  position: relative;

  .slick-slider {
    position: relative;
  }

  .slick-list {
    overflow: hidden;
    /* Keep items from starting underneath arrows */
    margin-left: 0;
    padding: 0;
    touch-action: pan-x;
    cursor: grab;
  }

  .slick-list.slick-dragging {
    cursor: grabbing;
  }

  &.has-prev .slick-list {
    padding-left: 60px;
  }

  &.has-next .slick-list {
    padding-right: 60px;
  }

  .slick-track {
    display: flex;
    align-items: center;
    margin-top: 14px;
  }

  .slick-slide {
    height: auto;
    display: flex !important;
    align-items: center;
    padding: 0;
    padding-right: 30px;
    pointer-events: auto;
  }

  .slick-slide > div {
    display: flex;
    pointer-events: auto;
  }

  /* Our global slick rule disables pointer-events on aria-hidden slides.
     For this nav we still want links clickable even when not the "active" slide. */
  .slick-slide[aria-hidden='true'] a {
    pointer-events: auto !important;
  }
`;

const ItemLink = styled(Link)`
  display: inline-block;
  white-space: nowrap;
  color: ${theme.colors.primary};
  text-decoration: none;
  /* padding: 0.4rem 0.35rem; */
  font-size: 0.95rem;
  position: relative;
  transition: color 220ms ease;

  &:hover {
    color: ${theme.colors.accent};
  }

  @keyframes siblingsUnderlineIn {
    from {
      transform: scaleX(0.7);
      opacity: 0.7;
    }
    to {
      transform: scaleX(1);
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &.active:after {
      animation: none;
    }
  }
`;

const ArrowButton = styled('button')`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 44px;
  width: 44px;
  border-radius: 999px;
  border: 0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  box-shadow: 0px 0px 10px -5px rgba(0, 0, 0, 0.25);
  transition: transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 220ms ease,
    box-shadow 220ms ease;

  svg {
    height: 14px;
    width: 14px;
  }

  &:hover {
    transform: translateY(-50%) scale(1.05);
    box-shadow: 0px 0px 12px -4px rgba(0, 0, 0, 0.28);
  }

  &:disabled {
    cursor: default;
    display: none;
    transform: translateY(-50%);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.accent};
    outline-offset: 2px;
  }

  &.prev {
    left: -6px;
    svg {
      transform: rotate(90deg);
    }
  }

  &.next {
    right: -6px;
    svg {
      transform: rotate(-90deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: translateY(-50%);
      box-shadow: 0px 0px 10px -5px rgba(0, 0, 0, 0.25);
    }
  }
`;

const Pagination = styled('div')`
  display: flex;
  justify-content: flex-start;
  margin-top: 0.5rem;
  max-width: 310px;
  width: 100%;

  ${theme.below.md} {
    max-width: 100%;
  }

  /* react-slick renders dots as <ul><li><button/></li></ul> */
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    gap: 6px;
    width: 100%;
  }

  li {
    flex: 1;
    margin: 0;
    padding: 0;
    display: flex;
  }
`;

const DotButton = styled('button')`
  flex: 1;
  height: 2px;
  border-radius: 999px;
  border: 0;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.55;
  position: relative;
  transition: opacity 220ms ease;

  &:before {
    content: '';
    width: 100%;
    height: 100%;
    border-radius: 999px;
    background: ${theme.colors.primary};
    opacity: 0.4;
    transition: background 220ms ease, opacity 220ms ease;
  }

  /* Increase click target height (esp. upwards) without changing layout */
  &:after {
    content: '';
    position: absolute;
    top: -14px;
    left: 0;
    right: 0;
    bottom: -8px;
    background: transparent;
  }

  &.active {
    opacity: 1;
  }

  &.active:before {
    background: ${theme.colors.accent};
    opacity: 1;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.accent};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:before {
      transition: none;
    }
  }
`;

const SlickArrow = props => {
  const { className, onClick, disabled, direction } = props;
  const isPrev = direction === 'prev';

  return (
    <ArrowButton
      type="button"
      className={cx(isPrev ? 'prev' : 'next', className)}
      onClick={onClick}
      disabled={disabled}
      aria-label={isPrev ? 'Previous' : 'Next'}
    >
      <Carrot />
    </ArrowButton>
  );
};

/**
 * Siblings slider using react-slick.
 *
 * - siblings: array of categories (expects { name, primaryRoute?.canonicalPath })
 * - startsWithExludation / endsWithExludation: arrays of prefixes/suffixes to filter out by name
 */
export const SubsOrSiblings = ({
  subsOrSiblings = [],
  ariaLabel = 'Sibling navigation',
  className,
  startsWithExludation = ['-'],
  endsWithExludation = ['*auto*']
}) => {
  const sliderRef = useRef(null);
  const didSwipeRef = useRef(false);
  const [current, setCurrent] = useState(0);

  const getPresentableName = (name = '') => {
    let editedName = name;
    startsWithExludation.forEach(prefix => {
      editedName = editedName.replace(prefix, '');
    });
    endsWithExludation.forEach(suffix => {
      editedName = editedName.replace(suffix, '');
    });

    return editedName.trim();
  };

  if (!subsOrSiblings || subsOrSiblings.length === 0) return null;

  const settings = {
    infinite: false,
    speed: 300,
    variableWidth: true,
    swipe: true,
    swipeToSlide: true,
    touchMove: true,
    touchThreshold: 3,
    draggable: true,
    waitForAnimate: false,
    focusOnSelect: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    dots: true,
    beforeChange: (_, next) => setCurrent(next),
    onSwipe: () => {
      didSwipeRef.current = true;
    },
    nextArrow: (
      <SlickArrow
        direction="next"
        disabled={current >= subsOrSiblings.length - 1}
      />
    ),
    prevArrow: <SlickArrow direction="prev" disabled={current <= 0} />,
    appendDots: dots => <Pagination>{dots}</Pagination>,
    customPaging: i => <DotButton className={cx(i <= current && 'active')} />
  };

  return (
    <Wrapper aria-label={ariaLabel} className={className}>
      <SliderShell
        className={cx(
          current > 0 && 'has-prev',
          current < subsOrSiblings.length - 1 && 'has-next'
        )}
      >
        <SlickSlider ref={sliderRef} {...settings}>
          {subsOrSiblings.map((it, idx) => {
            const key = it.id ?? `${it.name}-${idx}`;
            return (
              <div key={key}>
                <ItemLink
                  to={it.primaryRoute?.canonicalPath}
                  className={cx(idx === current && 'active')}
                  draggable={false}
                  onClick={e => {
                    if (didSwipeRef.current) {
                      didSwipeRef.current = false;
                      e.preventDefault();
                      return;
                    }
                    e.stopPropagation();
                  }}
                >
                  {getPresentableName(it.name)}
                </ItemLink>
              </div>
            );
          })}
        </SlickSlider>
      </SliderShell>
    </Wrapper>
  );
};
